import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../../components/index.js'
import { HeaderNavigationMenu } from './types.js'
import { useNavContext } from './NavProvider.js'
import { ResourceSubSubCategoryItemComp, ResourceSubSubCategoryContentComp } from './ResourceItem.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { getLocalizedPath } from '../../helpers/utils.js'

interface MenuItem {
  label: string
  href?: string
  onClick?: () => void
  active?: boolean
  icon?: string
  className?: string
}

const MenuItem = ({ label, onClick, active }: MenuItem) => {
  return (
    <button
      className="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent py-4 text-left"
      onClick={onClick}
      aria-expanded={active}
    >
      <div className="flex items-center gap-1">
        <span className="text-sm font-bold leading-[1.4]">{label}</span>
      </div>
      <svg
        className={`size-5 ${active ? 'rotate-90' : ''}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M6.91009 4.41009C7.23553 4.08466 7.76304 4.08466 8.08848 4.41009L13.0885 9.41009C13.4139 9.73553 13.4139 10.263 13.0885 10.5885L8.08848 15.5885C7.76304 15.9139 7.23553 15.9139 6.91009 15.5885C6.58466 15.263 6.58466 14.7355 6.91009 14.4101L11.3209 9.99929L6.91009 5.58848C6.58466 5.26304 6.58466 4.73553 6.91009 4.41009Z"
          fill="#080A0F"
        />
      </svg>
    </button>
  )
}

/**
 * 移动端资源位侧边栏下拉
 * @param resourceCategoriesMetadata 资源位分类元数据
 * @param resourcesMetadata 资源位元数据
 */
export const MobileResourceSidebarMenu = ({
  resourceCategoriesMetadata,
  resourcesMetadata,
}: {
  resourceCategoriesMetadata: any
  resourcesMetadata: any
}) => {
  const {
    currentMenu,
    setCurrentMenu,
    subSubCategory,
    setSubSubCategory,
    onSidebarNavClick,
    currentResourceMetadata,
    setCurrentResourceMetadata,
  } = useNavContext()
  const { locale = 'us' } = useAiuiContext()
  const [expandedSubcategory, setExpandedSubcategory] = useState<{ index: number; open: boolean }[]>([])
  const [expandedResourceCategories, setExpandedResourceCategories] = useState<{ index: number; open: boolean }[]>([])

  useEffect(() => {
    if (resourceCategoriesMetadata?.subcategories?.length) {
      setExpandedSubcategory(
        resourceCategoriesMetadata?.subcategories?.map((_: any, index: number) => ({
          index,
          open: false,
        }))
      )
    }
  }, [resourceCategoriesMetadata])

  useEffect(() => {
    if (subSubCategory?.subSubCategories?.length) {
      setExpandedResourceCategories(
        subSubCategory?.subSubCategories?.map((_: any, index: number) => ({
          index,
          open: index === 0,
        }))
      )
    }
  }, [subSubCategory])

  const handleSubSubCategoryClick = useCallback(
    (item: any) => {
      setSubSubCategory?.(item)
      const curResourcesMetadata =
        resourcesMetadata?.find(
          (resourceItem: any) => item?.label?.toLowerCase() === resourceItem.label?.toLowerCase()
        ) || {}
      setCurrentMenu && setCurrentMenu(HeaderNavigationMenu.Third)
      setCurrentResourceMetadata && setCurrentResourceMetadata(curResourcesMetadata)
    },
    [resourcesMetadata, setCurrentMenu, setSubSubCategory, setCurrentResourceMetadata]
  )

  const MobileResourceSidebarMenuComp = useMemo(() => {
    switch (currentMenu) {
      case HeaderNavigationMenu.Secondary:
        return (
          // 二级菜单
          <div className="tablet:px-8 laptop:px-16 flex h-full flex-col gap-8 p-4">
            <div>
              {resourceCategoriesMetadata?.subcategories?.map((item: any, index: number) => {
                return (
                  <div key={`${item.label}-${index}`}>
                    <MenuItem
                      label={item?.label}
                      active={expandedSubcategory.find(item => item.index === index)?.open}
                      onClick={() => {
                        handleSubSubCategoryClick(item)
                        onSidebarNavClick?.(item, index)
                      }}
                    />
                  </div>
                )
              })}
            </div>
            <div className="tablet:items-start flex flex-col items-center gap-4">
              {resourceCategoriesMetadata?.primary && (
                <Button
                  as="a"
                  href={`${getLocalizedPath(resourceCategoriesMetadata?.primary?.url, locale)}?ref=${resourceCategoriesMetadata?.primary?.label}_shopall`}
                  variant="primary"
                  size="lg"
                  className="tablet:w-auto w-full text-base"
                >
                  {resourceCategoriesMetadata?.primary?.label}
                </Button>
              )}
              {resourceCategoriesMetadata?.secondary && (
                <Button
                  as="a"
                  href={getLocalizedPath(resourceCategoriesMetadata?.secondary?.url, locale)}
                  variant="link"
                  size="lg"
                  className="tablet:w-auto tablet:px-0 w-full py-0 text-base"
                >
                  {resourceCategoriesMetadata?.secondary?.label}
                </Button>
              )}
            </div>
          </div>
        )
      case HeaderNavigationMenu.Third:
        return (
          // 三级菜单
          <div className="tablet:px-8 laptop:px-16 p-4">
            {subSubCategory?.subSubCategories ? (
              subSubCategory?.subSubCategories?.map((subSubCategoryItem: any, index: number) => {
                const curResourcesMetadata =
                  resourcesMetadata?.find(
                    (item: any) => item.label.toLowerCase() === subSubCategoryItem?.label?.toLowerCase()
                  ) || {}
                return (
                  <div key={`${subSubCategoryItem.label}-${index}`}>
                    {Reflect.ownKeys(subSubCategoryItem).length > 0 && (
                      <ResourceSubSubCategoryItemComp
                        matchResourcesMetadata={curResourcesMetadata}
                        onSubSubCategoryItemClick={() => {
                          onSidebarNavClick?.(subSubCategoryItem, index)
                          setExpandedResourceCategories(prev =>
                            prev.map((item, i) => ({ ...item, open: i === index ? !item.open : item.open }))
                          )
                        }}
                        expanded={!!expandedResourceCategories?.find(item => item.index === index)?.open}
                      />
                    )}
                    {expandedResourceCategories?.find(item => item.index === index)?.open && (
                      <ResourceSubSubCategoryContentComp matchResourcesMetadata={curResourcesMetadata} />
                    )}
                  </div>
                )
              })
            ) : (
              <>
                {Reflect.ownKeys(currentResourceMetadata).length > 0 && (
                  <ResourceSubSubCategoryItemComp matchResourcesMetadata={currentResourceMetadata} />
                )}
                <ResourceSubSubCategoryContentComp matchResourcesMetadata={currentResourceMetadata} />
              </>
            )}
          </div>
        )
      default:
        return null
    }
  }, [
    currentMenu,
    resourceCategoriesMetadata,
    resourcesMetadata,
    expandedSubcategory,
    expandedResourceCategories,
    subSubCategory,
    currentResourceMetadata,
    handleSubSubCategoryClick,
    onSidebarNavClick,
    locale,
  ])

  return MobileResourceSidebarMenuComp
}
